/**
 * Windowed Connection Hook
 *
 * Backing data layer for the entity grids' free-scroll UX. Instead of accumulating
 * pages sequentially (fetchMore) and blocking scroll until the next page loads, this
 * hook exposes a SPARSE, dense-length row array whose length equals the full dataset
 * (capped at `maxRows`). Rows that haven't been fetched are `undefined` (rendered as
 * skeletons); the grid drives fetching by reporting its visible range via `ensureRange`.
 *
 * Backend pagination cursors are plain integer offsets into an OpenSearch from/size
 * query (see ../coffee crm/api/utils.py — end_cursor = str(int(from_) + first)), so
 * any page can be fetched directly with `after: String(pageIndex * pageSize)`. The
 * OpenSearch `max_result_window` caps `from + size` at 10,000, hence `maxRows`.
 *
 * Uses `client.query({ fetchPolicy: 'no-cache' })` rather than a watched query +
 * fetchMore: nothing is written to the Apollo cache, so there is no unbounded cache
 * growth from deep scans and no evict-on-unmount/filter machinery. The tradeoff —
 * losing automatic mutation-response propagation into grid rows — is covered by the
 * pages' `cellEdits` overlay + `onFieldUpdate` wiring (see CLAUDE.md).
 *
 * Dual-mode: the hook is fully inert under `isLovablePreview()` or `skip` (no queries,
 * empty rows). The entity wrappers (use-contacts/use-companies/use-deals) own the
 * preview/dummy-data behaviour and pass `skip: !workspaceId`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, ApolloError } from '@apollo/client';
import type { DocumentNode } from '@apollo/client';
import { isLovablePreview } from '@/lib/utils/environment';
import {
  readWindowedPrefetch,
  readWindowedPrefetchInFlight,
  windowedPrefetchKey,
} from '@/lib/hooks/windowed-prefetch';

export const WINDOWED_SCROLL_PAGE_SIZE = 50;
export const WINDOWED_MAX_ROWS = 10_000;
export const MAX_CONCURRENT_PAGE_FETCHES = 3;
/** Max queued (not yet in-flight) pages retained during a fling; farthest are dropped. */
const MAX_QUEUE_RETENTION = 24;
/** After a page fails twice, wait this long before allowing another attempt. */
export const WINDOWED_RETRY_COOLDOWN_MS = 15_000;
/** Total attempts before a page is permanently blacklisted (until refresh/reset). */
export const WINDOWED_MAX_PAGE_ATTEMPTS = 4;

type Node = { id: string };

export interface WindowedExtractResult<TNode> {
  edges: Array<{ node?: TNode | null } | null>;
  totalCount?: number | null;
  /** Read only in `cursorPagination` mode. */
  pageInfo?: { endCursor?: string | null; hasNextPage?: boolean | null } | null;
}

export interface UseWindowedConnectionOptions<TData, TNode extends Node> {
  query: DocumentNode;
  extract: (data: TData) => WindowedExtractResult<TNode> | undefined;
  /** Full filter, including workspaceId/deleted. */
  filter: Record<string, unknown>;
  sort: unknown[];
  pageSize?: number;
  maxRows?: number;
  /** When true the hook is fully inert (kanban, no workspace, preview). */
  skip?: boolean;
  /**
   * Sequential CURSOR pagination for post-filtered connections whose cursor is
   * not a row offset — e.g. getDeals with compareDates + onlyWithChanges, where
   * the server reports `totalCount: -1` (unknown), `endCursor` is the OpenSearch
   * SCAN offset, and a page may hold fewer than `first` rows (even 0) while more
   * remain. In this mode: page N+1 is requested with `after` = page N's endCursor;
   * rows are appended in arrival order; a 0-row page that isn't the end is
   * followed immediately (keeps scanning); the data ends on hasNextPage=false, a
   * missing/non-advancing endCursor, or a cursor past `maxRows` (the OpenSearch
   * window). Only the sequential path (loadNextPage) applies — ensureRange is a
   * no-op. Off (default) = the offset-windowed behaviour, unchanged.
   */
  cursorPagination?: boolean;
}

export interface WindowedConnection<TNode extends Node> {
  /** Dense array of length `rowCount`; `undefined` entries are unloaded (skeleton) rows. */
  rows: (TNode | undefined)[];
  /** min(totalCount, maxRows), minus optimistically-removed rows. */
  rowCount: number;
  /** Raw connection totalCount (uncapped), null until the first page lands. */
  totalCount: number | null;
  /** True when totalCount exceeds the fetchable maxRows window. */
  cappedAtMax: boolean;
  /** True until the first page of the current generation has landed. */
  loading: boolean;
  error: ApolloError | null;
  /** Ensure the pages covering [start, end] (row indices) are fetched. */
  ensureRange: (start: number, end: number) => void;
  /** Sequential fallback used by dense (client-filtered) mode. */
  loadNextPage: () => void;
  hasMoreSequential: boolean;
  /**
   * Optimistically hide rows by id (tombstone). The sparse map stays in server
   * index space — removed ids are skipped when the dense `rows` array is
   * assembled — so page refetches (which keep serving the pre-delete offsets
   * until the delete mutation lands and the search index catches up) can never
   * resurrect a removed row.
   */
  removeRows: (ids: string[]) => void;
  /** Undo removeRows (e.g. the delete mutation failed): un-tombstone the ids. */
  restoreRows: (ids: string[]) => void;
  /** Invalidate all pages and refetch the visible window, keeping current rows/scroll. */
  refresh: () => void;
  /** Full reset to page 0 (filter/sort change). */
  reset: () => void;
  /**
   * Optimistically pin just-created rows above the server-paged rows. Pinned rows
   * live OUTSIDE the offset index space, so stale page refetches (e.g. the search
   * index hasn't caught up with the create yet) can never overwrite them. A pinned
   * row is dropped automatically once a fetched page returns the same id (the
   * server copy has arrived); reset() clears all pinned rows.
   */
  prependRows: (nodes: TNode[]) => void;
}

/** A usable server total — negative ("unknown", e.g. -1) or non-finite values are not. */
const isKnownTotal = (tc: unknown): tc is number =>
  typeof tc === 'number' && Number.isFinite(tc) && tc >= 0;

/** controllersRef key for the (single) in-flight cursor-mode request. */
const CURSOR_CONTROLLER_KEY = -1;

const isSortMappingError = (err: unknown): boolean => {
  const message = (err as { message?: string })?.message || '';
  return (
    (message.includes('mapping') && message.includes('sort')) ||
    message.includes('No mapping found') ||
    message.includes('search_phase_execution_exception')
  );
};

export function useWindowedConnection<TData, TNode extends Node>({
  query,
  extract,
  filter,
  sort,
  pageSize = WINDOWED_SCROLL_PAGE_SIZE,
  maxRows = WINDOWED_MAX_ROWS,
  skip = false,
  cursorPagination = false,
}: UseWindowedConnectionOptions<TData, TNode>): WindowedConnection<TNode> {
  const client = useApolloClient();

  // ---- Render-visible state --------------------------------------------------
  const [rowCount, setRowCount] = useState(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApolloError | null>(null);
  // Bumped whenever the sparse map changes so `rows` recomputes.
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  // Optimistically-created rows pinned above the server index space (see prependRows).
  const [pinnedRows, setPinnedRows] = useState<TNode[]>([]);
  const pinnedRef = useRef<TNode[]>([]);
  const setPinned = useCallback((next: TNode[]) => {
    pinnedRef.current = next;
    setPinnedRows(next);
  }, []);

  // ---- Internal state (refs — no re-render) ---------------------------------
  const dataRef = useRef<Map<number, TNode>>(new Map());
  const pageStateRef = useRef<Map<number, 'queued' | 'in-flight' | 'loaded'>>(new Map());
  const queueRef = useRef<number[]>([]);
  const inFlightRef = useRef(0);
  // One AbortController per in-flight page so superseded fetches can be cancelled
  // (reset/refresh, and pages the viewport has flung far past).
  const controllersRef = useRef<Map<number, AbortController>>(new Map());
  // Tombstoned (optimistically-removed) row ids — see removeRows. Deliberately
  // NOT cleared on reset/refresh: a deleted id never legitimately reappears, and
  // stale search-index pages may keep returning it for a while after the delete.
  const removedIdsRef = useRef<Set<string>>(new Set());
  const generationRef = useRef(0);
  const knownEndRef = useRef<number | null>(null);
  const totalCountRef = useRef<number | null>(null);
  const failuresRef = useRef<Map<number, number>>(new Map());
  const noRetryRef = useRef<Set<number>>(new Set());
  /** pageIndex → earliest timestamp at which a re-attempt is allowed (transient-failure cooldown). */
  const retryAtRef = useRef<Map<number, number>>(new Map());
  const pendingFirstRef = useRef(false);
  const lastRangeRef = useRef<{ start: number; end: number }>({ start: 0, end: pageSize });
  // ---- Cursor mode (see `cursorPagination`) ----
  const cursorModeRef = useRef(cursorPagination);
  cursorModeRef.current = cursorPagination;
  /** `after` for the next cursor page (null = first page, sent as "0"). */
  const cursorAfterRef = useRef<string | null>(null);
  /** Rows appended so far in cursor mode (dense index of the next row). */
  const cursorLoadedRef = useRef(0);
  const cursorDoneRef = useRef(false);
  const cursorInFlightRef = useRef(false);
  const cursorFailuresRef = useRef(0);

  // Latest filter/sort/config read at fetch time (kept in refs so callbacks stay stable).
  const filterRef = useRef(filter);
  const sortRef = useRef(sort);
  const pageSizeRef = useRef(pageSize);
  const maxRowsRef = useRef(maxRows);
  const queryRef = useRef(query);
  const extractRef = useRef(extract);
  const skipRef = useRef(skip);
  filterRef.current = filter;
  sortRef.current = sort;
  pageSizeRef.current = pageSize;
  maxRowsRef.current = maxRows;
  queryRef.current = query;
  extractRef.current = extract;
  skipRef.current = skip;

  const maxPageIndex = useCallback(
    () => Math.floor((maxRowsRef.current - 1) / pageSizeRef.current),
    [],
  );

  /** Recompute the visible rowCount from totalCount, knownEnd and the maxRows cap. */
  const recomputeRowCount = useCallback(() => {
    const cap = maxRowsRef.current;
    let rc: number;
    if (totalCountRef.current != null) {
      rc = Math.min(totalCountRef.current, cap);
    } else if (knownEndRef.current != null) {
      rc = knownEndRef.current;
    } else {
      rc = 0;
    }
    if (knownEndRef.current != null) rc = Math.min(rc, knownEndRef.current);
    setRowCount(Math.max(0, rc));
  }, []);

  // Forward declaration so fetchPage (defined first) can call drainQueue.
  const drainQueueRef = useRef<() => void>(() => {});

  /**
   * Abort every in-flight fetch. Each aborted request settles in its own `.catch`
   * (which decrements inFlightRef and frees the page state), so we only fire the
   * signals here and clear the controller map.
   */
  const abortAll = useCallback(() => {
    if (controllersRef.current.size === 0) return;
    for (const controller of controllersRef.current.values()) controller.abort();
    controllersRef.current = new Map();
  }, []);

  const fetchPage = useCallback(
    (pageIndex: number) => {
      const generation = generationRef.current;
      const size = pageSizeRef.current;
      const offset = pageIndex * size;
      if (offset >= maxRowsRef.current) {
        pageStateRef.current.delete(pageIndex);
        return;
      }
      const first = Math.min(size, maxRowsRef.current - offset);
      pageStateRef.current.set(pageIndex, 'in-flight');
      inFlightRef.current += 1;
      const controller = new AbortController();
      controllersRef.current.set(pageIndex, controller);

      client
        .query<TData>({
          query: queryRef.current,
          variables: {
            filter: filterRef.current,
            sort: sortRef.current,
            first,
            after: String(offset),
          },
          fetchPolicy: 'no-cache',
          context: { fetchOptions: { signal: controller.signal } },
        })
        .then((res) => {
          inFlightRef.current -= 1;
          controllersRef.current.delete(pageIndex);
          if (generation !== generationRef.current) {
            drainQueueRef.current();
            return;
          }
          const extracted = extractRef.current(res.data);
          const nodes = (extracted?.edges ?? [])
            .map((e) => e?.node)
            .filter((n): n is TNode => !!n);

          nodes.forEach((node, i) => dataRef.current.set(offset + i, node));
          pageStateRef.current.set(pageIndex, 'loaded');
          failuresRef.current.delete(pageIndex);
          retryAtRef.current.delete(pageIndex);

          // A pinned (optimistically-created) row now exists in server data — unpin
          // it so it isn't shown twice; the server copy is authoritative from here.
          if (pinnedRef.current.length > 0) {
            const incoming = new Set(nodes.map((n) => n.id));
            const remaining = pinnedRef.current.filter((p) => !incoming.has(p.id));
            if (remaining.length !== pinnedRef.current.length) setPinned(remaining);
          }

          const tc = extracted?.totalCount;
          if (typeof tc === 'number') {
            totalCountRef.current = tc;
            setTotalCount(tc);
          }
          // Fewer rows than requested ⇒ we've reached the end of the dataset.
          if (nodes.length < first) {
            const end = offset + nodes.length;
            knownEndRef.current =
              knownEndRef.current == null ? end : Math.min(knownEndRef.current, end);
          }
          recomputeRowCount();

          if (pendingFirstRef.current) {
            pendingFirstRef.current = false;
            setLoading(false);
          }
          setError(null);
          bump();
          drainQueueRef.current();
        })
        .catch((err: ApolloError) => {
          inFlightRef.current -= 1;
          controllersRef.current.delete(pageIndex);
          // Intentional cancellation (superseded window, reset/refresh):
          // free the slot and let a later ensureRange re-fetch this page. NOT an
          // error — don't setError, don't count it as a failure or blacklist it.
          const aborted =
            controller.signal.aborted ||
            (err as { name?: string })?.name === 'AbortError';
          if (aborted) {
            pageStateRef.current.delete(pageIndex);
            drainQueueRef.current();
            return;
          }
          if (generation !== generationRef.current) {
            drainQueueRef.current();
            return;
          }
          // Drop page state so a later ensureRange over this page retries on re-scroll.
          pageStateRef.current.delete(pageIndex);
          const fails = (failuresRef.current.get(pageIndex) ?? 0) + 1;
          failuresRef.current.set(pageIndex, fails);
          // Sort-mapping errors won't fix themselves — blacklist immediately. Other
          // failures are often transient (network blip mid-fling): after two quick
          // attempts, back off for a cooldown instead of leaving a permanent skeleton
          // band; blacklist only once the attempt cap is exhausted.
          if (isSortMappingError(err) || fails >= WINDOWED_MAX_PAGE_ATTEMPTS) {
            noRetryRef.current.add(pageIndex);
          } else if (fails >= 2) {
            retryAtRef.current.set(pageIndex, Date.now() + WINDOWED_RETRY_COOLDOWN_MS);
          }
          if (pendingFirstRef.current) {
            pendingFirstRef.current = false;
            setLoading(false);
          }
          setError(err);
          bump();
          drainQueueRef.current();
        });
    },
    [client, bump, recomputeRowCount, setPinned],
  );

  const drainQueue = useCallback(() => {
    if (queueRef.current.length === 0) return;
    // Prioritise pages nearest the latest requested range; trim the farthest.
    const center = (lastRangeRef.current.start + lastRangeRef.current.end) / 2;
    const size = pageSizeRef.current;
    const centerPage = center / size;
    queueRef.current.sort(
      (a, b) => Math.abs(a - centerPage) - Math.abs(b - centerPage),
    );
    if (queueRef.current.length > MAX_QUEUE_RETENTION) {
      const dropped = queueRef.current.splice(MAX_QUEUE_RETENTION);
      dropped.forEach((p) => {
        if (pageStateRef.current.get(p) === 'queued') pageStateRef.current.delete(p);
      });
    }
    // Cancel in-flight fetches for pages the viewport has moved far past, freeing
    // concurrency slots for the pages the user actually landed on. Each aborted
    // request cleans itself up in fetchPage's `.catch` (decrement + re-drain).
    if (controllersRef.current.size > 0) {
      for (const [p, controller] of controllersRef.current) {
        if (Math.abs(p - centerPage) > MAX_QUEUE_RETENTION) controller.abort();
      }
    }
    while (inFlightRef.current < MAX_CONCURRENT_PAGE_FETCHES && queueRef.current.length > 0) {
      const pageIndex = queueRef.current.shift()!;
      if (pageStateRef.current.get(pageIndex) !== 'queued') continue;
      fetchPage(pageIndex);
    }
  }, [fetchPage]);
  drainQueueRef.current = drainQueue;

  // Cursor mode: fetch the next page after the last endCursor (one request at a time).
  const fetchCursorPageRef = useRef<() => void>(() => {});
  const fetchCursorPage = useCallback(() => {
    if (skipRef.current || isLovablePreview()) return;
    if (cursorInFlightRef.current || cursorDoneRef.current) return;
    const generation = generationRef.current;
    const after = cursorAfterRef.current ?? '0';
    cursorInFlightRef.current = true;
    const controller = new AbortController();
    controllersRef.current.set(CURSOR_CONTROLLER_KEY, controller);

    client
      .query<TData>({
        query: queryRef.current,
        variables: {
          filter: filterRef.current,
          sort: sortRef.current,
          first: pageSizeRef.current,
          after,
        },
        fetchPolicy: 'no-cache',
        context: { fetchOptions: { signal: controller.signal } },
      })
      .then((res) => {
        if (generation !== generationRef.current) return; // superseded (reset cleared state)
        controllersRef.current.delete(CURSOR_CONTROLLER_KEY);
        cursorInFlightRef.current = false;
        cursorFailuresRef.current = 0;
        const extracted = extractRef.current(res.data);
        const nodes = (extracted?.edges ?? [])
          .map((e) => e?.node)
          .filter((n): n is TNode => !!n);

        const base = cursorLoadedRef.current;
        nodes.forEach((node, i) => dataRef.current.set(base + i, node));
        cursorLoadedRef.current = base + nodes.length;

        if (pinnedRef.current.length > 0) {
          const incoming = new Set(nodes.map((n) => n.id));
          const remaining = pinnedRef.current.filter((p) => !incoming.has(p.id));
          if (remaining.length !== pinnedRef.current.length) setPinned(remaining);
        }

        const tc = extracted?.totalCount;
        if (isKnownTotal(tc)) {
          totalCountRef.current = tc;
          setTotalCount(tc);
        }

        const next = extracted?.pageInfo?.endCursor ?? null;
        const nextOffset = next != null ? Number(next) : NaN;
        const exhausted =
          extracted?.pageInfo?.hasNextPage === false ||
          next == null ||
          next === after ||
          (Number.isFinite(nextOffset) && nextOffset >= maxRowsRef.current);
        if (exhausted) {
          cursorDoneRef.current = true;
          knownEndRef.current = cursorLoadedRef.current;
        } else {
          cursorAfterRef.current = next;
        }
        setRowCount(cursorLoadedRef.current);

        // An empty page that isn't the end only means this request's scan budget ran
        // out — keep scanning (and keep the first-load spinner up) rather than
        // reporting "no rows".
        const keepScanning = !exhausted && nodes.length === 0;
        if (pendingFirstRef.current && !keepScanning) {
          pendingFirstRef.current = false;
          setLoading(false);
        }
        setError(null);
        bump();
        if (keepScanning) fetchCursorPageRef.current();
      })
      .catch((err: ApolloError) => {
        if (generation !== generationRef.current) return;
        controllersRef.current.delete(CURSOR_CONTROLLER_KEY);
        cursorInFlightRef.current = false;
        const aborted =
          controller.signal.aborted || (err as { name?: string })?.name === 'AbortError';
        if (aborted) return;
        cursorFailuresRef.current += 1;
        // Unrecoverable (sort mapping) or out of attempts: stop at what we have
        // instead of letting the load-more sentinel retry forever.
        if (isSortMappingError(err) || cursorFailuresRef.current >= WINDOWED_MAX_PAGE_ATTEMPTS) {
          cursorDoneRef.current = true;
          knownEndRef.current = cursorLoadedRef.current;
        }
        if (pendingFirstRef.current) {
          pendingFirstRef.current = false;
          setLoading(false);
        }
        setError(err);
        bump();
      });
  }, [client, bump, setPinned]);
  fetchCursorPageRef.current = fetchCursorPage;

  const enqueue = useCallback(
    (pageIndex: number) => {
      if (pageIndex < 0 || pageIndex > maxPageIndex()) return;
      if (noRetryRef.current.has(pageIndex)) return;
      const retryAt = retryAtRef.current.get(pageIndex);
      if (retryAt != null) {
        if (Date.now() < retryAt) return; // cooling down after transient failures
        retryAtRef.current.delete(pageIndex);
      }
      if (pageStateRef.current.has(pageIndex)) return; // queued / in-flight / loaded
      pageStateRef.current.set(pageIndex, 'queued');
      queueRef.current.push(pageIndex);
    },
    [maxPageIndex],
  );

  /** start/end in server (offset-paged) index space — excludes pinned rows. */
  const ensureServerRange = useCallback(
    (start: number, end: number) => {
      if (skipRef.current || isLovablePreview()) return;
      // Cursor mode has no offset index space to window over (sequential only).
      if (cursorModeRef.current) return;
      const size = pageSizeRef.current;
      const clampedStart = Math.max(0, start);
      const clampedEnd = Math.min(end, maxRowsRef.current - 1);
      if (clampedEnd < clampedStart) return;
      lastRangeRef.current = { start: clampedStart, end: clampedEnd };
      const startPage = Math.floor(clampedStart / size);
      const endPage = Math.floor(clampedEnd / size);
      for (let p = startPage; p <= endPage; p++) enqueue(p);
      drainQueue();
    },
    [enqueue, drainQueue],
  );

  const ensureRange = useCallback(
    (start: number, end: number) => {
      // Callers pass indices into `rows`, which includes pinned rows at the top —
      // shift into server (offset-paged) index space before mapping to pages.
      const pinnedCount = pinnedRef.current.length;
      ensureServerRange(start - pinnedCount, end - pinnedCount);
    },
    [ensureServerRange],
  );

  const clearAll = useCallback(() => {
    abortAll();
    dataRef.current = new Map();
    pageStateRef.current = new Map();
    queueRef.current = [];
    failuresRef.current = new Map();
    noRetryRef.current = new Set();
    retryAtRef.current = new Map();
    knownEndRef.current = null;
    totalCountRef.current = null;
    cursorAfterRef.current = null;
    cursorLoadedRef.current = 0;
    cursorDoneRef.current = false;
    cursorInFlightRef.current = false;
    cursorFailuresRef.current = 0;
  }, [abortAll]);

  const reset = useCallback(() => {
    generationRef.current += 1;
    clearAll();
    if (pinnedRef.current.length > 0) setPinned([]);
    setError(null);

    if (cursorModeRef.current) {
      // Cursor mode: no prefetch seed (its key/offset model doesn't apply).
      pendingFirstRef.current = true;
      setLoading(true);
      setTotalCount(null);
      setRowCount(0);
      bump();
      fetchCursorPage();
      return;
    }

    // react-query-style prefetch seed: if the first page for this exact
    // (query, filter, sort, pageSize) was prefetched (e.g. right after a Lead
    // Finder build, see windowed-prefetch.ts), paint it synchronously so the grid
    // shows rows immediately instead of a loader. This never REPLACES the network
    // pass below — page 0 is still enqueued, so a slightly-stale seed (OpenSearch
    // indexing lag) is corrected as soon as the authoritative fetch lands. Purely
    // best-effort: any mismatch just means no instant paint.
    const prefetchKey = windowedPrefetchKey({
      query: queryRef.current,
      filter: filterRef.current,
      sort: sortRef.current,
      pageSize: pageSizeRef.current,
    });
    // Paint a completed seed into dataRef and mark loading done. Shared by the
    // synchronous hit below and the in-flight-attach path.
    //
    // The seed is BEST-EFFORT and may be partial/stale: a prefetch fired during
    // OpenSearch indexing lag can capture fewer rows than the list actually has
    // (e.g. 2 of 4). So we deliberately DON'T set knownEnd from it — knownEnd only
    // ever shrinks (Math.min in fetchPage), so a stale `reachedEnd` would cap
    // rowCount permanently and the authoritative refetch could never grow it back.
    // The always-following page-0 fetch is what establishes the real end/count.
    const paintSeed = (s: { nodes: TNode[]; totalCount: number | null; reachedEnd: boolean }) => {
      s.nodes.forEach((node, i) => dataRef.current.set(i, node));
      if (typeof s.totalCount === 'number') {
        totalCountRef.current = s.totalCount;
        setTotalCount(s.totalCount);
      } else {
        setTotalCount(null);
      }
      pendingFirstRef.current = false;
      setLoading(false);
      recomputeRowCount();
      bump();
    };

    const seed = readWindowedPrefetch<TNode>(prefetchKey);
    if (seed && seed.nodes.length > 0) {
      // Paint the seed rows now, but DON'T mark page 0 as 'loaded' — we leave its
      // page state unset so the enqueue(0) below still fires a network fetch that
      // overwrites these same dataRef indices in place (no flicker: loading is
      // already false and the fresh rows land at 0..n). That is the cache-and-network
      // contract: show the seed instantly, revalidate against the server.
      paintSeed(seed);
      enqueue(0);
      drainQueue();
      return;
    }

    // No completed seed. If a prefetch for this exact key is still in flight (it was
    // fired earlier, e.g. at Lead Finder build completion, and started before this
    // grid mounted), ATTACH to it instead of firing a duplicate identical query:
    // await it, then paint page 0 from the stored seed. Since it started first, it
    // resolves sooner than a fresh fetch would. Falls back to a normal fetch if it
    // resolves empty/failed. Never blocks: loading stays true meanwhile.
    const inflight = readWindowedPrefetchInFlight(prefetchKey);
    pendingFirstRef.current = true;
    setLoading(true);
    setTotalCount(null);
    setRowCount(0);
    bump();
    if (inflight) {
      const gen = generationRef.current;
      // Reserve page 0 so the virtualizer's ensureRange can't fire a duplicate fetch
      // for it while we await the prefetch (enqueue is a no-op for a known page).
      pageStateRef.current.set(0, 'in-flight');
      void inflight.then((stored) => {
        if (gen !== generationRef.current) return; // superseded by a newer reset
        const s = stored ? readWindowedPrefetch<TNode>(prefetchKey) : undefined;
        // Release the page-0 reservation either way.
        pageStateRef.current.delete(0);
        // Paint the seed for a fast first render if present, but ALWAYS revalidate
        // page 0: the prefetch we attached to may itself have been partial/stale
        // (indexing lag), so the authoritative fetch establishes the real rows/count.
        if (s && s.nodes.length > 0) paintSeed(s);
        enqueue(0);
        drainQueue();
      });
      return;
    }

    // Seed page 0 so we learn the total; the virtualizer will ensure the rest.
    enqueue(0);
    drainQueue();
  }, [clearAll, bump, enqueue, drainQueue, setPinned, recomputeRowCount, fetchCursorPage]);

  const refresh = useCallback(() => {
    // Cursor pages can't be re-requested in place (each cursor depends on the
    // previous page's scan), so a refresh restarts from the top.
    if (cursorModeRef.current) {
      reset();
      return;
    }
    // Keep existing rows visible (no flicker); invalidate page state and refetch
    // whatever is currently in view. Old in-flight responses are dropped by generation.
    generationRef.current += 1;
    abortAll();
    pageStateRef.current = new Map();
    queueRef.current = [];
    failuresRef.current = new Map();
    noRetryRef.current = new Set();
    retryAtRef.current = new Map();
    const { start, end } = lastRangeRef.current;
    ensureServerRange(start, end);
    // Always refresh page 0 too (totalCount + top of list).
    enqueue(0);
    drainQueue();
  }, [ensureServerRange, enqueue, drainQueue, abortAll, reset]);

  const removeRows = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      // Pinned rows aren't in the offset index space — drop them directly.
      if (pinnedRef.current.some((p) => idSet.has(p.id))) {
        setPinned(pinnedRef.current.filter((p) => !idSet.has(p.id)));
      }
      // Tombstone instead of compacting the sparse map: the map stays in server
      // index space (loaded pages remain valid, no refetch needed), and the ids
      // are skipped at rows-assembly time. Compact-and-refetch — the previous
      // approach — raced the caller's delete mutation: the refetched pages still
      // contained the rows (mutation in flight / search-index lag) and wrote
      // them straight back, resurrecting "deleted" rows in the grid.
      ids.forEach((id) => removedIdsRef.current.add(id));
      bump();
    },
    [bump, setPinned],
  );

  const restoreRows = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      let changed = false;
      for (const id of ids) {
        if (removedIdsRef.current.delete(id)) changed = true;
      }
      if (changed) bump();
    },
    [bump],
  );

  const prependRows = useCallback(
    (nodes: TNode[]) => {
      if (nodes.length === 0 || skipRef.current || isLovablePreview()) return;
      const existing = new Set(pinnedRef.current.map((p) => p.id));
      const fresh = nodes.filter((n) => !existing.has(n.id));
      if (fresh.length === 0) return;
      setPinned([...fresh, ...pinnedRef.current]);
    },
    [setPinned],
  );

  const loadNextPage = useCallback(() => {
    if (skipRef.current || isLovablePreview()) return;
    if (cursorModeRef.current) {
      fetchCursorPage();
      return;
    }
    const size = pageSizeRef.current;
    const nextPage = Math.floor(dataRef.current.size / size);
    enqueue(nextPage);
    drainQueue();
  }, [enqueue, drainQueue, fetchCursorPage]);

  // (Re)initialise whenever filter/sort/pageSize/maxRows change or skip toggles.
  const configKey = useMemo(
    () => JSON.stringify({
      filter, sort, pageSize, maxRows,
      // Only present when on, so the key is unchanged for offset-mode callers.
      ...(cursorPagination ? { cursorPagination } : {}),
    }),
    [filter, sort, pageSize, maxRows, cursorPagination],
  );

  useEffect(() => {
    if (isLovablePreview() || skip) {
      // Inert: drop any prior generation's work and report not-loading.
      generationRef.current += 1;
      clearAll();
      if (pinnedRef.current.length > 0) setPinned([]);
      pendingFirstRef.current = false;
      setLoading(false);
      setRowCount(0);
      setTotalCount(null);
      bump();
      return;
    }
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, skip]);

  // Assemble the dense render array, skipping tombstoned (removed) rows. Skipping
  // shortens the array — the shift-compaction the old removeRows did eagerly now
  // happens here, against whatever the sparse map currently holds, so stale page
  // data can never bring a removed row back. `removedLoaded` counts tombstones
  // still present in the map so the reported counts drop with the visible rows.
  const { rows, removedLoaded } = useMemo(() => {
    const removed = removedIdsRef.current;
    const arr: (TNode | undefined)[] = [...pinnedRows];
    let removedLoaded = 0;
    for (let i = 0; i < rowCount; i++) {
      const node = dataRef.current.get(i);
      if (node && removed.size > 0 && removed.has(node.id)) {
        removedLoaded += 1;
        continue;
      }
      arr.push(node);
    }
    return { rows: arr, removedLoaded };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowCount, version, pinnedRows]);

  const hasMoreSequential =
    !skip && !isLovablePreview() && (
      cursorPagination
        ? !cursorDoneRef.current
        : dataRef.current.size < rowCount
    );

  const cappedAtMax = totalCount != null && totalCount > maxRows;

  return {
    rows,
    rowCount: rows.length,
    // Until the server copy of a pinned row is indexed, the connection totalCount
    // doesn't include it — add pinned so footers/counters reflect reality. Same
    // in reverse for tombstoned rows the server still counts: subtract them.
    totalCount:
      totalCount == null
        ? null
        : Math.max(0, totalCount - removedLoaded) + pinnedRows.length,
    cappedAtMax,
    loading,
    error,
    ensureRange,
    loadNextPage,
    hasMoreSequential,
    removeRows,
    restoreRows,
    refresh,
    reset,
    prependRows,
  };
}
